/**
 * Owen side panel — dynamic analysis, readiness score, and privacy transparency.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, CircleHelp, X } from 'lucide-react';
import TypewriterText from './TypewriterText';
import ApplyMilestones from './ApplyMilestones';
import { OWEN_PRIVATE_PANEL_TITLE, OWEN_SHARED_PANEL_TITLE } from '../../config/owenPrivacy';

const OwenCollapsibleSection = ({
  title,
  count,
  open,
  onToggle,
  panelId,
  collapsedHint,
  className = '',
  children,
}) => (
  <div className={`lk-conv-owen__section${className ? ` ${className}` : ''}`}>
    <button
      type="button"
      className="lk-conv-owen__section-toggle"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={panelId}
    >
      <span className="lk-conv-owen__section-toggle-label">{title}</span>
      <span
        className="lk-conv-owen__section-count"
        aria-label={`${count} ${count === 1 ? 'item' : 'items'}`}
      >
        {count}
      </span>
      <ChevronDown
        size={14}
        className={`lk-conv-owen__section-chevron${open ? ' lk-conv-owen__section-chevron--open' : ''}`}
        aria-hidden
      />
    </button>

    {open ? (
      <div id={panelId} className="lk-conv-owen__section-panel">
        {children}
      </div>
    ) : (
      collapsedHint && (
        <p className="lk-conv-owen__section-collapsed-hint">{collapsedHint}</p>
      )
    )}
  </div>
);

const OwenInsightBar = ({
  insight,
  optionHelp,
  readinessScore,
  analyzing,
  sharedEntries = [],
  ignoredLabels = [],
  milestoneIndex = 0,
  onDismissHelp,
}) => {
  const helpText = optionHelp?.text?.trim() || '';
  const showOptionHelp = Boolean(helpText);
  const showInsight = analyzing || Boolean(insight?.trim());
  const [helpDismissed, setHelpDismissed] = useState(false);
  const helpKey = showOptionHelp
    ? `${optionHelp.label}-${helpText.slice(0, 48)}`
    : '';

  useEffect(() => {
    setHelpDismissed(false);
  }, [helpKey]);

  const showHelpPanel = showOptionHelp && !helpDismissed;
  const isQuestionLevelHelp = optionHelp?.label === 'About this question';
  const sharedCount = sharedEntries.length;
  const ignoresCount = ignoredLabels.length;

  const [sharedOpen, setSharedOpen] = useState(false);
  const [ignoresOpen, setIgnoresOpen] = useState(false);

  useEffect(() => {
    if (!showHelpPanel) return;
    setSharedOpen(false);
    setIgnoresOpen(false);
  }, [showHelpPanel]);

  const handleDismissHelp = () => {
    setHelpDismissed(true);
    onDismissHelp?.();
  };

  const sharedCollapsedHint = sharedCount > 0
    ? `${sharedCount} answer${sharedCount === 1 ? '' : 's'} — expand to review`
    : 'Your answers will appear here as you go — expand anytime';

  const ignoresCollapsedHint = ignoresCount > 0
    ? `${ignoresCount} private field${ignoresCount === 1 ? '' : 's'} in your file — expand to view`
    : 'Sensitive details stay in your LoanKnow file only — expand anytime';

  return (
    <aside className="lk-conv-owen">
      <div className="lk-conv-owen__pinned">
        <div className="lk-conv-owen__head">
          <img className="lk-conv-owen__avatar" src="/assets/owen-avatar.png" alt="" aria-hidden />
          <div className="lk-conv-owen__head-text">
            <span className="lk-conv-owen__name">Owen</span>
            <span className="lk-conv-owen__role">
              {showHelpPanel ? 'Explaining' : 'Your guide'}
            </span>
          </div>
          <div
            className="lk-conv-owen__score"
            aria-label={`Readiness score ${readinessScore} out of 100`}
          >
            <span className="lk-conv-owen__score-value">{readinessScore}</span>
            <span className="lk-conv-owen__score-label">readiness</span>
          </div>
        </div>

        {(showInsight || showHelpPanel) && (
          <div className="lk-conv-owen__callouts">
            {showInsight && (
              <div
                className="lk-conv-owen__callout lk-conv-owen__callout--insight"
                aria-live="polite"
              >
                {analyzing ? (
                  <p className="lk-conv-owen__callout-text">I'm reviewing that…</p>
                ) : (
                  <p className="lk-conv-owen__callout-text">
                    <TypewriterText
                      key={insight.slice(0, 48)}
                      text={insight}
                      active
                      charDelayMs={10}
                    />
                  </p>
                )}
              </div>
            )}

            {showHelpPanel && (
              <div
                className="lk-conv-owen__callout lk-conv-owen__callout--help"
                aria-live="polite"
                aria-labelledby="owen-help-badge"
              >
                <div className="lk-conv-owen__callout-header">
                  <div className="lk-conv-owen__help-badge" id="owen-help-badge">
                    <CircleHelp size={14} strokeWidth={2} aria-hidden />
                    <span>Help</span>
                  </div>
                  <button
                    type="button"
                    className="lk-conv-owen__callout-dismiss lk-conv-owen__callout-dismiss--help"
                    onClick={handleDismissHelp}
                    aria-label="Close help"
                  >
                    <X size={14} aria-hidden />
                  </button>
                </div>
                <p className="lk-conv-owen__help-subject">
                  {isQuestionLevelHelp ? 'About this question' : 'About your selection'}
                </p>
                {!isQuestionLevelHelp && (
                  <p className="lk-conv-owen__help-choice">{optionHelp.label}</p>
                )}
                <p className="lk-conv-owen__callout-text lk-conv-owen__callout-text--help">
                  <TypewriterText
                    key={`help-${helpKey}`}
                    text={helpText}
                    active
                    charDelayMs={8}
                  />
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="lk-conv-owen__scroll">
        <OwenCollapsibleSection
          title={OWEN_SHARED_PANEL_TITLE}
          count={sharedCount}
          open={sharedOpen}
          onToggle={() => setSharedOpen((v) => !v)}
          panelId="owen-shared-panel"
          collapsedHint={sharedCollapsedHint}
        >
          {sharedCount > 0 ? (
            <ul className="lk-conv-owen__shared-list">
              {sharedEntries.map((item) => (
                <li key={`${item.topic}-${item.answer}`}>
                  <span className="lk-conv-owen__shared-topic">{item.topic}</span>
                  <span className="lk-conv-owen__shared-answer-wrap">
                    <span className="lk-conv-owen__shared-answer">{item.answer}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="lk-conv-owen__shared-empty">
              Your non-sensitive answers will appear here as we go.
            </p>
          )}
        </OwenCollapsibleSection>

        <OwenCollapsibleSection
          title={OWEN_PRIVATE_PANEL_TITLE}
          count={ignoresCount}
          open={ignoresOpen}
          onToggle={() => setIgnoresOpen((v) => !v)}
          panelId="owen-ignores-panel"
          collapsedHint={ignoresCollapsedHint}
          className="lk-conv-owen__section--bordered"
        >
          {ignoresCount > 0 ? (
            <ul className="lk-conv-owen__ignores-list">
              {ignoredLabels.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="lk-conv-owen__ignores-empty">
              Sensitive details stay in your LoanKnow file only.
            </p>
          )}
        </OwenCollapsibleSection>
      </div>

      <div className="lk-conv-owen__footer">
        <ApplyMilestones activeIndex={milestoneIndex} />
      </div>
    </aside>
  );
};

export default OwenInsightBar;
